import tagColors from '../../lib/tagColors';

export type RGB = {
  r: number;
  g: number;
  b: number;
};

/**
 * Calculates the relative luminance of a given hex color string based on the
 * sRGB color space.
 *
 * @param {string} hex - A hexadecimal color string (e.g., "#RRGGBB"*/
export function getLuminance(hex: string): number {
  // Remove the hash and normalize the string
  let cleanHex = hex.replace(/^#/, '');

  // Support short notation (#fff)
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // Must be 6 characters long
  if (cleanHex.length !== 6) {
    return 0;
  }

  const num = parseInt(cleanHex, 16);
  if (isNaN(num)) return 0;

  // Extract r, g, b explicitly and safely
  const r: number = (num >> 16) & 0xff;
  const g: number = (num >> 8) & 0xff;
  const b: number = num & 0xff;

  // Force type as number
  const [lr, lg, lb]: [number, number, number] = [r, g, b].map((value) => {
    const scaled = value / 255;
    return scaled <= 0.03928 ? scaled / 12.92 : Math.pow((scaled + 0.055) / 1.055, 2.4);
  }) as [number, number, number];

  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/**
 * Converts a hexadecimal color code to an RGB object.
 *
 * @param {string} hex - The hexadecimal color code, which can be in a 3- or 6-character format.
 * @return {RGB | null} An object containing the red (r), green (g), and blue (b) values,
 * or null if the input is not a valid hexadecimal color code.
 */
export function hexToRgb(hex: string): RGB | null {
  const clean = (hex || '').replace(/^#/, '').trim().toLowerCase();

  if (clean.length === 6) {
    const bigint = parseInt(clean, 16);
    if (isNaN(bigint)) return null;
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

  if (clean.length === 3) {
    // TypeScript qui vuole garanzia: forziamo il tipo dell’array
    const chars = clean.split('') as [string, string, string];
    return {
      r: parseInt(chars[0] + chars[0], 16),
      g: parseInt(chars[1] + chars[1], 16),
      b: parseInt(chars[2] + chars[2], 16),
    };
  }

  return null;
}

/**
 * Calculates the contrast ratio between two colors based on their luminance values.
 *
 * @param color1 The first color in a string format (e.g., hexadecimal, RGB, or any format supported by getLuminance).
 * @param color2 The second color in a string format (e.g., hexadecimal, RGB, or any format supported by getLuminance).
 * @return A number representing the contrast ratio between the two colors. Higher values indicate greater contrast.
 */
export function getContrastRatio(color1: string, color2: string): number {
  const luminance1 = getLuminance(color1) + 0.05;
  const luminance2 = getLuminance(color2) + 0.05;
  return luminance1 > luminance2 ? luminance1 / luminance2 : luminance2 / luminance1;
}

function lightenColor({ r, g, b }: { r: number; g: number; b: number }, amount: number) {
  return {
    r: r + ((255 - r) * amount) / 100,
    g: g + ((255 - g) * amount) / 100,
    b: b + ((255 - b) * amount) / 100,
  };
}

function darkenColor({ r, g, b }: { r: number; g: number; b: number }, amount: number) {
  return {
    r: r * (1 - amount / 100),
    g: g * (1 - amount / 100),
    b: b * (1 - amount / 100),
  };
}

function desaturateColor({ r, g, b }: { r: number; g: number; b: number }, factor: number) {
  const gray = r * 0.3 + g * 0.59 + b * 0.11;
  return {
    r: r + factor * (gray - r),
    g: g + factor * (gray - g),
    b: b + factor * (gray - b),
  };
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }): string {
  return (
    '#' +
    [r, g, b]
      .map((x) =>
        Math.max(0, Math.min(255, Math.round(x)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

export interface TagPresentationStyle {
  backgroundColor: string;
  border: string;
  color: string;
}

export type TagPresentationStyleOptions = {
  /** When true, transparent fill — color only on text + border (dark UI). */
  darkMode?: boolean;
};

const MIN_TAG_TEXT_CONTRAST = 4.5;
const DARK_UI_SURFACE = '#0a0a0a';

function normalizeHexColor(hex: string): string {
  const trimmed = (hex || '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function resolveTagDarkMode(explicit?: boolean): boolean {
  if (typeof explicit === 'boolean') {
    return explicit;
  }
  if (typeof document === 'undefined') {
    return false;
  }
  return document.documentElement.classList.contains('dark');
}

function ensureTextContrastOnSurface(textHex: string, textRgb: RGB, surfaceHex: string): string {
  let readableTextColor = textHex;
  if (getContrastRatio(readableTextColor, surfaceHex) >= MIN_TAG_TEXT_CONTRAST) {
    return readableTextColor;
  }

  let foregroundRgb = { ...textRgb };
  const surfaceIsLight = getLuminance(surfaceHex) > 0.5;
  const adjustColor = surfaceIsLight ? darkenColor : lightenColor;
  const adjustmentStep = surfaceIsLight ? 10 : 12;

  for (let iteration = 0; iteration < 15; iteration++) {
    foregroundRgb = adjustColor(foregroundRgb, adjustmentStep);
    readableTextColor = rgbToHex(foregroundRgb);
    if (getContrastRatio(readableTextColor, surfaceHex) >= MIN_TAG_TEXT_CONTRAST) {
      break;
    }
  }

  return readableTextColor;
}

/**
 * Tag pill colors.
 * Light: opaque pastel fill with guaranteed text contrast.
 * Dark: transparent fill — colored text + border only (pastel fills wash out on black UI).
 */
export function getTagPresentationStyle(
  textColor: string | undefined,
  options?: TagPresentationStyleOptions,
): TagPresentationStyle {
  const darkMode = resolveTagDarkMode(options?.darkMode);
  const normalizedTextColor = normalizeHexColor(textColor ?? '');
  const textRgb = hexToRgb(normalizedTextColor);

  if (darkMode) {
    const darkFallback: TagPresentationStyle = {
      backgroundColor: 'transparent',
      border: '1px solid #9aa0a6',
      color: '#e8eaed',
    };

    if (!textRgb) {
      return darkFallback;
    }

    const readableTextColor = ensureTextContrastOnSurface(
      normalizedTextColor,
      textRgb,
      DARK_UI_SURFACE,
    );

    return {
      backgroundColor: 'transparent',
      border: `1px solid ${readableTextColor}`,
      color: readableTextColor,
    };
  }

  const fallbackStyle: TagPresentationStyle = {
    backgroundColor: '#f1f3f4',
    border: '1px solid #dadce0',
    color: '#5F6368',
  };

  if (!textRgb) {
    return fallbackStyle;
  }

  const backgroundColor = getContrastBackgroundColorForText(normalizedTextColor, {
    ignorePreset: true,
    minContrast: MIN_TAG_TEXT_CONTRAST,
    pastel: true,
  });

  const readableTextColor = ensureTextContrastOnSurface(
    normalizedTextColor,
    textRgb,
    backgroundColor,
  );

  const borderColor = rgbToHex(darkenColor(textRgb, 28));

  return {
    backgroundColor,
    border: `1px solid ${borderColor}`,
    color: readableTextColor,
  };
}

/**
 * Determines the background color (either light or dark) that provides the best contrast for the given text color.
 *
 * @param {string} textColor - The text color in a valid color format (e.g., HEX, RGB).
 * @return {string} The background color (either light or dark) that maximizes contrast with the provided text color.
 */
export function getContrastBackgroundColorForText(
  textColor: string,
  options?: {
    pastel?: boolean;
    minContrast?: number;
    ignorePreset?: boolean;
  },
): string {
  const normalizedTextColor = normalizeHexColor(textColor);

  // Preset pairs are used only when callers opt in (legacy paths).
  if (!options?.ignorePreset) {
    const defaultValues = tagColors.find((colorPair) => colorPair.text === normalizedTextColor);
    if (defaultValues) {
      return defaultValues.bg;
    }
  }

  const pastel = options?.pastel ?? true;
  const minContrast = options?.minContrast ?? 7;

  const rgb = hexToRgb(normalizedTextColor);
  if (!rgb) {
    return '#f1f3f4';
  }

  const luminance = getLuminance(normalizedTextColor);
  const isDark = luminance < 0.5;

  let bgRgb = { ...rgb };

  // Pastel effect
  if (pastel) {
    bgRgb = isDark ? lightenColor(bgRgb, 85) : darkenColor(desaturateColor(bgRgb, 0.3), 10);
  } else {
    bgRgb = isDark ? lightenColor(rgb, 40) : darkenColor(rgb, 20);
  }

  // Ensure contrast
  let iterations = 0;
  while (getContrastRatio(rgbToHex(rgb), rgbToHex(bgRgb)) < minContrast && iterations < 10) {
    bgRgb = isDark ? lightenColor(bgRgb, 8) : darkenColor(bgRgb, 5);
    iterations++;
  }

  return rgbToHex(bgRgb);
}
